import { MicrophoneIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'
import localizeCurrency from '../helpers/localizeCurrency'
import localizeStripePrice, {
  getStripePriceAmountAndCurrency,
} from '../helpers/localizeStripePrice'
import { Plan, StripePrice } from '../types'
import PlanCardCountdown from './PlanCardCountdown'
import SubscriptionForm from './SubscriptionForm'

interface Props {
  bestOffer?: boolean
  countdownUntil: number
  currency: string
  isSelected?: boolean
  isSubmitting: boolean
  onSelect?: () => void
  perk?: string
  plan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  stripePriceFull: StripePrice
  stripePriceIntro?: StripePrice | undefined
  trialPeriodDays?: number
}

export default function PlanCard({
  bestOffer,
  countdownUntil,
  currency,
  isSelected,
  isSubmitting,
  onSelect,
  perk,
  plan,
  setIsSubmitting,
  stripePriceFull,
  stripePriceIntro,
  trialPeriodDays,
}: Props) {
  const { t } = useTranslation()

  if (!stripePriceFull) return null

  const isOneTime = plan === 'one-time'
  const stripePrice = stripePriceIntro ?? stripePriceFull

  const { stripePriceCurrency } = getStripePriceAmountAndCurrency({
    stripePrice,
    userCurrency: currency,
  })

  const getDaysFor = (stripePrice: StripePrice) => {
    if (!stripePrice.recurring) return 1
    const interval = stripePrice.recurring.interval
    const intervalCount = stripePrice.recurring.interval_count

    switch (interval) {
      case 'week':
        return intervalCount * 7
      case 'month':
        return intervalCount * 30
      case 'year':
        return intervalCount * 365
      default:
        return intervalCount
    }
  }

  const localizeStripePricePerDay = (stripePrice: StripePrice) => {
    return localizeStripePrice({
      fraction: getDaysFor(stripePrice),
      stripePrice,
      userCurrency: currency,
    })
  }

  const localizeStripePricePerInterval = ({
    interval,
    intervalCount,
    stripePrice,
  }: {
    interval: string
    intervalCount: number
    stripePrice: StripePrice
  }) => {
    const stripePriceInterval = stripePrice.recurring?.interval
    const stripePriceIntervalCount = stripePrice.recurring?.interval_count

    if (
      interval === stripePriceInterval &&
      intervalCount === stripePriceIntervalCount
    ) {
      return localizeStripePrice({
        stripePrice,
        userCurrency: currency,
      })
    }

    if (interval === 'month' && stripePriceInterval === 'year') {
      return localizeStripePrice({
        fraction: 12,
        stripePrice,
        userCurrency: currency,
      })
    }

    return localizeStripePrice({
      stripePrice,
      userCurrency: currency,
    })
  }

  const planLabel = isOneTime
    ? t('pages.checkout.plans.oneTimePurchase')
    : `${stripePrice.recurring!.interval_count}-${t(
        `pages.checkout.plans.${stripePrice.recurring!.interval}`,
        {
          count: stripePrice.recurring!.interval_count,
        },
      )}`

  return (
    <div
      className={twMerge(
        'text-custom-black cursor-pointer rounded-b-3xl border',
        isSelected
          ? 'border-primary opacity-100'
          : 'border-primary/40 opacity-50',
        bestOffer ? 'rounded-t-lg' : 'rounded-t-3xl',
      )}
      onClick={onSelect}
    >
      {bestOffer && (
        <div className="bg-primary rounded-t-md p-1 text-center font-bold text-white uppercase">
          {t('pages.checkout.plans.mostPopular')}
        </div>
      )}

      <div className="overflow-hidden rounded-3xl p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2 sm:gap-4">
          <div className="flex items-start gap-2 sm:gap-4">
            {onSelect && (
              <div className="mt-1 sm:mt-2">
                <div className="border-primary flex h-6 w-6 items-center justify-center rounded-full border-2 bg-transparent sm:h-7 sm:w-7">
                  {isSelected && (
                    <div className="bg-primary h-3 w-3 rounded-full sm:h-3.5 sm:w-3.5" />
                  )}
                </div>
              </div>
            )}

            <div className="text-left">
              <div className="mb-2 flex items-center gap-2 sm:mb-3 sm:gap-3">
                <h3 className="text-xl font-bold sm:text-2xl md:text-3xl">
                  {planLabel}
                </h3>

                {!isOneTime &&
                  (trialPeriodDays ||
                    stripePriceIntro?.metadata.trial === 'true') && (
                    <span className="bg-primary inline-flex flex-col items-center rounded-lg px-3 py-1.5 text-xs font-bold text-white sm:rounded-xl sm:px-5 sm:py-2 sm:text-sm">
                      {trialPeriodDays
                        ? t('pages.checkout.trialPlans.trialBadge', {
                            count: trialPeriodDays,
                          })
                        : t('pages.checkout.plans.trial')}
                    </span>
                  )}
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                {isOneTime && (
                  <p className="text-base text-[#B4B4B4] line-through sm:text-lg md:text-xl">
                    {localizeStripePrice({
                      fraction: 0.5,
                      stripePrice: stripePriceFull,
                      userCurrency: currency,
                    })}
                  </p>
                )}

                {!isOneTime && stripePriceIntro && (
                  <p className="text-base text-[#B4B4B4] line-through sm:text-lg md:text-xl">
                    {localizeStripePricePerInterval({
                      interval: stripePriceIntro.recurring!.interval,
                      intervalCount: stripePriceIntro.recurring!.interval_count,
                      stripePrice: stripePriceFull,
                    })}
                  </p>
                )}

                {trialPeriodDays && (
                  <p className="text-base text-[#B4B4B4] line-through sm:text-lg md:text-xl">
                    {localizeStripePrice({
                      stripePrice: stripePriceFull,
                      userCurrency: currency,
                    })}
                  </p>
                )}

                <p className="text-lg font-bold sm:text-xl md:text-2xl">
                  {trialPeriodDays
                    ? t('pages.checkout.trialPlans.todayPrice', {
                        price: localizeCurrency({
                          amountInCents: 0,
                          currency: stripePriceCurrency,
                        }),
                      })
                    : localizeStripePrice({
                        stripePrice,
                        userCurrency: currency,
                      })}
                </p>

                {isOneTime && (
                  <span className="bg-primary rounded-lg px-2 py-1 text-xs font-bold text-white sm:px-3 sm:py-1.5 sm:text-sm">
                    -50%
                  </span>
                )}
              </div>

              {isOneTime && (
                <p className="text-primary mt-1 text-sm font-semibold">
                  {t('pages.checkout.plans.oneTimeAccess')}
                </p>
              )}

              {trialPeriodDays && (
                <p className="mt-1 text-sm font-medium text-gray-500">
                  {t('pages.checkout.trialPlans.renewsAt', {
                    price: localizeStripePrice({
                      stripePrice: stripePriceFull,
                      userCurrency: currency,
                    }),
                  })}
                </p>
              )}
            </div>
          </div>

          {!isOneTime && !trialPeriodDays && (
            <div className="flex-shrink-0 text-right">
              <p className="mb-0.5 text-3xl leading-none font-bold sm:mb-1 sm:text-4xl md:text-5xl">
                {localizeStripePricePerDay(stripePrice)}
              </p>

              <p className="text-xs font-normal text-[#B4B4B4] sm:text-sm md:text-base">
                {t('pages.checkout.plans.perDay')}
              </p>
            </div>
          )}
        </div>

        {perk && (
          <div className="from-primary/15 to-primary/5 border-primary/20 mt-3 flex items-center gap-2.5 rounded-2xl border bg-gradient-to-r px-3 py-2.5 sm:mt-4 sm:gap-3 sm:px-4 sm:py-3">
            <span className="bg-primary/15 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full sm:h-9 sm:w-9">
              <MicrophoneIcon className="text-primary h-4 w-4 sm:h-5 sm:w-5" />
            </span>

            <p className="text-primary text-sm leading-snug font-semibold sm:text-base">
              {perk}
            </p>
          </div>
        )}

        {(!onSelect || isSelected) && (
          <PlanCardCountdown countdownUntil={countdownUntil} />
        )}

        {!onSelect && (
          <div className="mt-6 space-y-4">
            <SubscriptionForm
              buttonClassName="custom-button-primary uppercase"
              buttonTitle={
                isOneTime ? t('buttons.payNow') : t('buttons.subscribeNow')
              }
              currency={stripePriceCurrency}
              stripePriceFull={stripePriceFull}
              isSubmitting={isSubmitting}
              plan={plan}
              priceId={stripePrice.id}
              setIsSubmitting={setIsSubmitting}
              trialPeriodDays={trialPeriodDays}
            />

            {isOneTime ? (
              <p className="text-center text-sm font-bold">
                {t('pages.checkout.plans.moneyBackGuarantee')}
              </p>
            ) : (
              <>
                <p className="text-center text-sm">
                  {t('pages.checkout.plans.cancelAnytime')}
                </p>

                <p className="text-center text-sm font-bold">
                  {t('pages.checkout.plans.moneyBackGuarantee')}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
